import type { InvoiceData } from "@/apis/gql/types";
import type { Row } from "@tanstack/react-table";
import type React from "react";
import { memo, useMemo, useState } from "react";

import {
  exportInvoicesByMonth,
  generateDatevFormatFiles,
} from "@/apis/custom/customRoutes";
import { Button } from "@/components/ui/button";
import { useBrowserSessionId } from "@/hooks/useBrowserSessionId";
import * as invoiceStatus from "@/lib/constants/invoiceStatus";
import { useClearBackgroundJobs } from "@/providers/background-jobs";

import type { InvoiceTypeString } from "@/lib/constants/invoice-type";
import { useInvoiceTableInstanceContext } from "../table-instance-provider";
import ExportAlertDialog from "./ExportAlertDialog";
import { MonthSelectionDialog } from "./month-selection-dialog";

type Props = {
  label?: string;
  children?: (value: { handlePress: () => Promise<void> }) => React.ReactNode;
  showExportAllWarning?: boolean;
  disable?: boolean;
  invoiceType?: InvoiceTypeString;
};

export const DatevExportButton = memo(
  ({
    label,
    children,
    showExportAllWarning = true,
    disable = false,
    invoiceType,
  }: Props) => {
    const { table } = useInvoiceTableInstanceContext();
    const clearBackgroundJobs = useClearBackgroundJobs();

    const selectedRows = table ? table.getFilteredSelectedRowModel().rows : [];
    const selectedInvoiceIds =
      selectedRows.length > 0
        ? selectedRows.map((row) => row.original?.invoice.invoiceId)
        : [];
    const selectedInvoiceRows = selectedRows.length > 0 ? selectedRows : [];

    const allRows = table ? table.getFilteredRowModel().rows : [];

    const [isAlertOpen, setAlertOpen] = useState(false);
    const [exportDialog, setExportDialog] = useState({
      title: "",
      description: "",
      onConfirm: () => {},
      disallowExport: false,
    });

    const sessionId = useBrowserSessionId() ?? "";

    const [isMonthSelectionOpen, setIsMonthSelectionOpen] = useState(false);

    const hasUnreviewed = useMemo(() => {
      return (selectedInvoiceRows as any).some((row: any) => {
        return !invoiceStatus.InvoiceProcessingSteps.IS_REVIEWED.includes(
          row.original?.invoice.status,
        );
      });
    }, [selectedInvoiceRows]);

    const hasExported = useMemo(() => {
      return (selectedInvoiceRows as any).some(
        (row: any) =>
          row.original?.invoice.status === invoiceStatus.InvoiceStatus.EXPORTED,
      );
    }, [selectedInvoiceRows]);

    const handleGroupedExportConfirm = async (
      groupedInvoices: Record<string, string[]>,
    ) => {
      clearBackgroundJobs();
      await exportInvoicesByMonth(groupedInvoices, sessionId);
      setIsMonthSelectionOpen(false);
    };

    const handleGenerateDatevFormatFiles = async (
      groupedInvoices: Record<string, string[]>,
    ) => {
      await generateDatevFormatFiles({ invoiceGroups: groupedInvoices });
      setIsMonthSelectionOpen(false);
    };

    const showExportConfirmation = (
      title: string,
      description: string,
      onConfirm: any,
      disallowExport: boolean,
    ) => {
      setExportDialog({ title, description, onConfirm, disallowExport });
      setAlertOpen(true);
    };

    const handleExportToDatev = async (
      _invoices: Row<{ invoice: InvoiceData }>[],
    ) => {
      const invoices = _invoices.map((row) => row.original?.invoice);

      if (invoices.length > 0) {
        setIsMonthSelectionOpen(true);
      }
    };

    const handleExportInvoicesToDATEV = async () => {
      if (hasUnreviewed) {
        showExportConfirmation(
          "DATEV Export",
          "Auswahl enthält Rechnungen mit Status Zu Prüfen. Es können nur Rechnungen im Status Fertig exportiert werden. Bitte überprüfen Sie die Auswahl.",
          () => setAlertOpen(false),
          true,
        );
        return;
      }

      if (hasExported) {
        showExportConfirmation(
          "DATEV Export",
          "Auswahl enthält Rechnungen mit Status Exportiert. Wollen Sie diese nochmal exportieren?",
          () => {
            handleExportToDatev(selectedRows);
            setAlertOpen(false);
          },
          false,
        );
        return;
      }

      if (selectedInvoiceIds.length > 0) {
        handleExportToDatev(selectedRows);
      } else {
        if (!showExportAllWarning) {
          handleExportToDatev(allRows);
          return;
        }

        showExportConfirmation(
          "Wollen Sie wirklich alle Rechnungen exportieren?",
          "Wenn keine Rechnungen ausgewählt sind, werden alle auf einmal exportiert. Sonst werden nur die ausgewählten Rechnungen exportiert.",
          () => {
            handleExportToDatev(allRows);
            setAlertOpen(false);
          },
          false,
        );
      }
    };

    return (
      <div>
        <ExportAlertDialog
          isOpen={isAlertOpen}
          title={exportDialog.title}
          description={exportDialog.description}
          onConfirm={exportDialog.onConfirm}
          onCancel={() => setAlertOpen(false)}
          disallowExport={exportDialog.disallowExport}
        />
        {children ? (
          children({ handlePress: handleExportInvoicesToDATEV })
        ) : (
          <Button onClick={handleExportInvoicesToDATEV} disabled={disable}>
            {label}
          </Button>
        )}
        <MonthSelectionDialog
          isOpen={isMonthSelectionOpen}
          onGenerateDatevFormatFiles={handleGenerateDatevFormatFiles}
          onConfirm={handleGroupedExportConfirm}
          onCancel={() => {
            setIsMonthSelectionOpen(false);
          }}
          invoiceIds={selectedInvoiceIds}
          exportAll={selectedInvoiceIds.length === 0}
          invoiceType={invoiceType}
        />
      </div>
    );
  },
);
